/**
 * Style C — Warm amber glass tint
 * Same box shape but: warm dark amber bg, no border, very soft radius.
 * Feels like the box was always part of the frame.
 */
import { useCurrentFrame, useVideoConfig, interpolate, Easing, AbsoluteFill } from "remotion";
import { loadFont } from "@remotion/google-fonts/Inter";
import { loadFont as loadDevanagari } from "@remotion/google-fonts/NotoSansDevanagari";

const { fontFamily } = loadFont("normal", { weights: ["400", "600", "700", "800"], subsets: ["latin"] });
const { fontFamily: devFont } = loadDevanagari("normal", { weights: ["400", "700"], subsets: ["devanagari"] });

const GOLD = "#D4A853";
const BG_WARM = "rgba(20,9,1,0.72)";

const WarmTag: React.FC<{
  name: string; nameHindi?: string; role: string; period?: string;
  frame: number; fps: number; durationInFrames: number;
  side: "left" | "right"; bottom?: number;
}> = ({ name, nameHindi, role, period, frame, fps, durationInFrames, side, bottom = 60 }) => {
  const fadeIn = interpolate(frame, [0, Math.round(0.35 * fps)], [0, 1], {
    extrapolateRight: "clamp", extrapolateLeft: "clamp",
    easing: Easing.bezier(0.16, 1, 0.3, 1),
  });
  const slideX = interpolate(frame, [0, Math.round(0.4 * fps)], [side === "left" ? -30 : 30, 0], {
    extrapolateRight: "clamp", extrapolateLeft: "clamp",
    easing: Easing.bezier(0.16, 1, 0.3, 1),
  });
  const fadeOut = interpolate(frame, [durationInFrames - Math.round(0.5 * fps), durationInFrames], [1, 0], {
    extrapolateLeft: "clamp", extrapolateRight: "clamp",
  });

  return (
    <div style={{
      position: "absolute", bottom, [side]: 28,
      opacity: fadeIn * fadeOut,
      transform: `translateX(${slideX}px)`,
      background: BG_WARM,
      borderRadius: side === "left" ? "0 6px 6px 0" : "6px 0 0 6px",
      // single very subtle warm border — same color family as gold, very low opacity
      borderLeft: side === "left" ? "2px solid rgba(212,168,83,0.22)" : "none",
      borderRight: side === "right" ? "2px solid rgba(212,168,83,0.22)" : "none",
      padding: "11px 16px 11px 14px",
      maxWidth: 310,
    }}>
      <div style={{ color: "#fff", fontSize: 18, fontWeight: 800, fontFamily, lineHeight: 1.15 }}>
        {name}
      </div>
      {nameHindi && (
        <div style={{ color: "rgba(255,220,150,0.5)", fontSize: 12, fontWeight: 400, fontFamily: devFont, marginTop: 2 }}>
          {nameHindi}
        </div>
      )}
      {/* Thin warm divider */}
      <div style={{ width: "100%", height: 1, background: "rgba(212,168,83,0.18)", margin: "6px 0 5px" }} />
      <div style={{ color: "rgba(212,168,83,0.9)", fontSize: 11, fontWeight: 600, fontFamily, textTransform: "uppercase", letterSpacing: "0.8px" }}>
        {role}
      </div>
      {period && (
        <div style={{ color: "rgba(255,220,150,0.35)", fontSize: 10, fontFamily, marginTop: 2 }}>
          {period}
        </div>
      )}
    </div>
  );
};

const WarmYear: React.FC<{
  display: string; displayLatin?: string; context?: string;
  frame: number; fps: number; durationInFrames: number;
}> = ({ display, displayLatin, context, frame, fps, durationInFrames }) => {
  const fadeIn = interpolate(frame, [0, Math.round(0.35 * fps)], [0, 1], {
    extrapolateRight: "clamp", extrapolateLeft: "clamp",
  });
  const slideX = interpolate(frame, [0, Math.round(0.4 * fps)], [-30, 0], {
    extrapolateRight: "clamp", extrapolateLeft: "clamp",
    easing: Easing.bezier(0.16, 1, 0.3, 1),
  });
  const fadeOut = interpolate(frame, [durationInFrames - Math.round(0.5 * fps), durationInFrames], [1, 0], {
    extrapolateLeft: "clamp", extrapolateRight: "clamp",
  });
  const isDevanagari = /[ऀ-ॿ]/.test(display);

  return (
    <div style={{
      position: "absolute", top: 28, left: 0,
      opacity: fadeIn * fadeOut,
      transform: `translateX(${slideX}px)`,
      background: BG_WARM,
      borderRadius: "0 6px 6px 0",
      borderLeft: "2px solid rgba(212,168,83,0.22)",
      padding: "10px 20px 10px 14px",
      maxWidth: 320,
    }}>
      <div style={{
        color: GOLD, fontSize: isDevanagari ? 22 : 26, fontWeight: 800,
        fontFamily: isDevanagari ? devFont : fontFamily, lineHeight: 1,
      }}>
        {display}
      </div>
      {displayLatin && (
        <div style={{ color: "rgba(212,168,83,0.55)", fontSize: 10, fontWeight: 600, fontFamily, marginTop: 3, letterSpacing: "0.5px" }}>
          {displayLatin}
        </div>
      )}
      {context && (
        <div style={{ color: "rgba(255,220,150,0.7)", fontSize: 11, fontFamily, marginTop: 6, lineHeight: 1.4, maxWidth: 260 }}>
          {context}
        </div>
      )}
    </div>
  );
};

export const StyleC_Preview: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps, durationInFrames } = useVideoConfig();

  return (
    <AbsoluteFill style={{ pointerEvents: "none" }}>
      <WarmTag
        name="Nara and Narayana" nameHindi="नर और नारायण"
        role="Sages performing penance at Badri Ashram" period="Mythological Era"
        frame={frame} fps={fps} durationInFrames={durationInFrames}
        side="left" bottom={60}
      />
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
